<i18n>
{
  "fr": {
    "save-feedback": "Sauvegardé",
    "invalid-url": "Veuillez spécifier une adresse URL valide. L'addresse doit débuter par «\u00A0http://\u00A0» ou «\u00A0https://\u00A0».",
    "invalid-integer": "Veuillez spécifier un nombre valide, sans décimales.",
    "invalid-amount": "Veuillez spécifier un montant valide, avec ou sans les cents. Utilisez le point (.) comme séparateur décimal.",
    "invalid-decimal": "Veuillez spécifier un nombre valide, avec ou sans décimales. Utilisez le point (.) comme séparateur décimal.",
    "reference-indicator": "Référence :"
  },
  "en": {
    "save-feedback": "Saved",
    "invalid-url": "Please enter a valid URL address. The address must start with \"http://\" or \"https://\".",
    "invalid-integer": "Please enter a valid number, without decimals.",
    "invalid-amount": "Please enter a valid amount, with or without cents. Use a period (.) as the decimals separator.",
    "invalid-decimal": "Please enter a valid number, with or without decimals. Use a period (.) as the decimals separator.",
    "reference-indicator": "Reference:"
  }
}
</i18n>
<template>
  <div class="question">
    <s-field
      class="has-max-width-768"
      ref="field"
      :label="question.label"
      :label-for="question.uniqueKey"
      :message="question.helpText"
      :value="currentValue"
      :rules="editorRules"
      validate-on-input
    >
      <div>
        <div class="is-flex">
          <component
            style="flex: 1 1 0"
            :is="editorComponent"
            v-bind="editorComponentProps"
            :id="question.uniqueKey"
            :value="currentValue"
            @input="(value) => (currentValue = value)"
            @save="() => saveAnswer()"
          />

          <div class="is-flex has-text-success ml-2 save-feedback" :class="{ active: showFeedback }">
            <b-icon class="is-size-3 mb-1" icon="check-circle"></b-icon>
            <span class="is-size-7">{{ $t('save-feedback') }}</span>
          </div>
        </div>
        <div v-if="question.indicator" class="is-size-7">
          {{ $t('reference-indicator') }}
          <router-link :to="indicatorLinkLocation" target="_blank">
            <b-icon icon="link"></b-icon>
            {{ question.indicator.title }}
          </router-link>
        </div>
      </div>
    </s-field>
  </div>
</template>
<script>
import {
  QUESTION_TYPE_TEXT,
  QUESTION_TYPE_URL,
  QUESTION_TYPE_INTEGER,
  QUESTION_TYPE_MONEY,
  QUESTION_TYPE_PERCENT,
  QUESTION_TYPE_RICH_TEXT,
  QUESTION_TYPE_YES_NO,
  QUESTION_TYPE_ATTACHMENT,
  QUESTION_TYPE_CONTEXT
} from '@/consts/enums'
import FormHelpers from '@/helpers/form'
import BusinessService from '@/services/business'

import SField from '@/components/form/field'
import SimpleQuestion from './question-types/simple'
import RichTextQuestion from './question-types/rich-text'
import YesNoQuestion from './question-types/yes-no'
import FileQuestion from './question-types/file'

const FEEDBACK_TIMEOUT = 1500

export default {
  components: { SField },
  props: {
    question: Object,
    language: String
  },
  data() {
    return {
      currentValue: this.question.answer,
      showFeedback: false
    }
  },
  computed: {
    editorComponent() {
      switch (this.question.type) {
        case QUESTION_TYPE_TEXT:
        case QUESTION_TYPE_URL:
        case QUESTION_TYPE_INTEGER:
        case QUESTION_TYPE_MONEY:
        case QUESTION_TYPE_PERCENT:
          return SimpleQuestion
        case QUESTION_TYPE_RICH_TEXT:
        case QUESTION_TYPE_CONTEXT:
          return RichTextQuestion
        case QUESTION_TYPE_YES_NO:
          return YesNoQuestion
        case QUESTION_TYPE_ATTACHMENT:
          return FileQuestion
        default:
          return null
      }
    },
    editorComponentProps() {
      switch (this.question.type) {
        case QUESTION_TYPE_MONEY:
          return { addon: '$' }
        case QUESTION_TYPE_PERCENT:
          return { addon: '%' }
        default:
          return null
      }
    },
    editorRules() {
      switch (this.question.type) {
        case QUESTION_TYPE_URL:
          return [(v) => FormHelpers.stringEmpty(v) || FormHelpers.isWebsiteUrl(v) || this.$t('invalid-url')]
        case QUESTION_TYPE_INTEGER:
          return [(v) => FormHelpers.stringEmpty(v) || FormHelpers.isInteger(v) || this.$t('invalid-integer')]
        case QUESTION_TYPE_MONEY:
          return [(v) => FormHelpers.stringEmpty(v) || FormHelpers.isAmount(v) || this.$t('invalid-amount')]
        case QUESTION_TYPE_PERCENT:
          return [(v) => FormHelpers.stringEmpty(v) || FormHelpers.isDecimal(v) || this.$t('invalid-decimal')]
        default:
          return null
      }
    },
    indicatorLinkLocation() {
      if (!this.question) return ''
      if (!this.question.indicator) return ''

      if (this.question.indicator.__typename === 'ProjectIndicatorGraphType')
        return {
          name: this.$consts.urls.URL_INDICATOR_PROJECT,
          params: { projectId: this.question.indicator.project.id, id: this.question.indicator.id }
        }

      return { name: this.$consts.urls.URL_INDICATOR_GENERAL, params: { id: this.question.indicator.id } }
    }
  },
  methods: {
    async saveAnswer() {
      if (this.validate() !== true) return

      const newValue = (this.currentValue == null ? '' : this.currentValue).trim()
      const previousValue = (this.question.answer == null ? '' : this.question.answer).trim()

      if (newValue === previousValue) return

      await this.$apollo.mutate({
        mutation: this.$options.mutation.SaveAnswer,
        variables: {
          businessId: BusinessService.getCurrentBusiness().id,
          language: this.language,
          questionKey: this.question.uniqueKey,
          answer: newValue
        }
      })

      this.showFeedback = true

      if (this.feedbackTimeout) clearTimeout(this.feedbackTimeout)
      this.feedbackTimeout = setTimeout(() => (this.showFeedback = false), FEEDBACK_TIMEOUT)
    },
    validate() {
      return this.$refs.field.validate()
    }
  },
  beforeDestroy() {
    this.saveAnswer()
  },
  watch: {
    'question.answer'(newAnswer) {
      this.currentValue = newAnswer
    }
  }
}
</script>

<graphql>
mutation SaveAnswer($businessId: ID!, $language: ContentLanguage!, $questionKey: String!, $answer: String) {
  updateQuestionnaireAnswer(input: { businessId: $businessId, language: $language, questionKey: $questionKey, answer: $answer }) {
    question {
      id
      answer(businessId: $businessId)
    }
  }
}
</graphql>

<style lang="scss">
.question {
  &:not(:last-child) {
    margin-bottom: 24px;

    @include widescreen {
      margin-bottom: 32px;
    }
  }
  .label {
    margin-bottom: 16px;
  }

  .help {
    margin-right: 62px; // Feedback icon width
  }
}

.save-feedback {
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;

  opacity: 0;
  transition: opacity 0.1s;

  pointer-events: none;

  &.active {
    opacity: 1;
  }
}
</style>